import Icon, { SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useAntdTable } from 'ahooks';
import { Card, Col, message, Row, Select, Table, Button, Input, Space, Popover, Spin } from 'antd';
import dayjs from 'lib/dayjs';
import { useMemo, useState } from 'react';
import { BlueTag, RedTag } from 'components/styled/tags';
import { useTranslate } from '@tolgee/react';
import { waPhoneList, IPhoneListRsp, waBuyerComment } from 'services/api/waBuyers';
import toLowerCase from 'utils/toLowerCase';
import { ReactComponent as iconMore } from 'views/Setting/BlockList/assets/more.svg';
import useRegionList from 'components/StoreSelector/hooks/useRegionList';
import WaConcatBtn from 'components/ContactWhatsapp/WaConcatBtn';
import InlineEditor from 'views/Setting/Shops/InlineEditor';
import DetailDrawer, { EllipsisStyle, BuyerRow } from './DetailDrawer';
import ModalBind, { TPlatform } from './ModalBind';
import ModalUpload from './ModalUpload';
import { CountryFlagIcon } from 'components/CountryFlagIcon';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { formatNumber } from 'utils/numberFormat';

const Container = styled.div`
  padding: 16px;
  .total {
    margin-top: 16px;
    margin-bottom: 16px;
  }
  div.ant-typography {
    margin-bottom: 0;
  }
`;

const MoreShop = styled.div`
  span:first-child {
    margin-right: 8px;
    color: var(--fett-text-tertiary);
  }
  .anticon {
    width: 16px;
    height: 16px;
  }
`;

const RowStyle = css`
  &:hover td {
    background-color: var(--fett-erp-card-bg);
    cursor: pointer;
  }
`;

const MaxBuyersTip = styled.div`
  text-align: center;
  color: var(--fett-text-tertiary);
  margin: 16px 0;
  width: 100%;
`;

export const numDisplay = (num?: number) => {
  return formatNumber(num, '0,0.00');
};

const WhatsappBuyers = () => {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const [platformFilter, setPlatformFilter] = useState<TPlatform | undefined>();
  const [regionFilter, setRegionFilter] = useState<General.UpperCaseRegion | undefined>();
  const [keywordFilter, setKeywordFilter] = useState<string | undefined>();

  const [clickRowData, setClickRowData] = useState<IPhoneListRsp['data'][0]>();
  const [clickRowIndx, setClickRowIndx] = useState(-1);

  const [visDrawer, setVisDrawer] = useState(false);

  const [visModalBuyer, setVisModalBuyer] = useState(false);

  const [visModalUpload, setVisModalUpload] = useState(false);

  const [spinning, setSpinning] = useState(false);
  const [editing, setEditing] = useState(false);

  const { regionsMap } = useRegionList();

  const [refreshAgain, setRefreshAgain] = useState(0);

  const { tableProps } = useAntdTable(
    ({ current, pageSize }) =>
      waPhoneList({
        pageNum: `${current}`,
        pageSize: `${pageSize}`,
        keyword: keywordFilter,
        platform: platformFilter,
        region: platformFilter ? regionFilter : undefined,
      }).then((res) => {
        const rowNum = Number(res.totalRows);
        return {
          total: rowNum > 1000 ? 1000 : rowNum,
          list: res.data,
        };
      }),
    {
      refreshDeps: [platformFilter, regionFilter, keywordFilter, refreshAgain],
      onError() {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );

  const columns = useMemo(() => {
    return [
      {
        dataIndex: 'phone',
        title: t('WhatsApp.table.buyerMobile'),
        render: (value: string) => {
          return value
            ? `${value.slice(0, 2)}${'*'.repeat(
                value.length - 4 > 0 ? value.length - 4 : 0,
              )}${value.slice(value.length - 2)}`
            : '';
        },
      },
      {
        dataIndex: 'region',
        title: t('WhatsappBuyers.地区'),
      },
      {
        dataIndex: 'remark',
        editable: true,
        title: t('trade.买家备注'),
        render: (value: string, record: IPhoneListRsp['data'][0]) => {
          return (
            <InlineEditor
              clickCb={(e) => {
                setVisDrawer(false);
                setEditing(true);
                e.stopPropagation();
              }}
              value={value}
              maxLength={64}
              onSubmit={(value) => {
                setSpinning(true);
                return waBuyerComment({ phone: record.phone, remark: value })
                  .then((resp) => {
                    setRefreshAgain((num) => num + 1);
                    message.success(t('translation.设置成功'));
                    return resp;
                  })
                  .catch(() => {
                    message.error(t('translation.操作失败，请稍后再试'));
                    return false;
                  })
                  .finally(() => {
                    setSpinning(false);
                    setEditing(false);
                  });
              }}
            />
          );
        },
      },
      {
        dataIndex: 'buyers',
        title: t('WhatsappBuyers.关联平台'),
        render(value: IPhoneListRsp['data'][0]['buyers']) {
          const getBuyerRow = (start: number, end?: number) => {
            return value.slice(start, end).map(({ storeName, platform, region }) => (
              <BuyerRow>
                {platform === 'SHOPEE' ? (
                  <RedTag>{platform}</RedTag>
                ) : (
                  <BlueTag>{platform}</BlueTag>
                )}
                <CountryFlagIcon title={getRegionLabel(region)} region={region} />
                <span title={storeName} className={EllipsisStyle}>
                  {storeName}
                </span>
              </BuyerRow>
            ));
          };
          const UI = (
            <Space direction="vertical" size={8}>
              {getBuyerRow(0, 2)}
              {value.length - 2 > 0 ? (
                <Popover
                  content={
                    <Space direction="vertical" size={8}>
                      {getBuyerRow(2)}
                    </Space>
                  }
                >
                  <MoreShop>
                    <span>{t('group.更多x家店铺', { total: value.length - 2 })}</span>
                    <Icon component={iconMore} />
                  </MoreShop>
                </Popover>
              ) : null}
            </Space>
          );
          return UI;
        },
      },
      {
        dataIndex: 'lastChatTime',
        title: t('WhatsappBuyers.最近联系'),
        render(value: string, record: IPhoneListRsp['data'][0]) {
          const time = Number(value);
          return time > 0 ? (
            <>
              <Space direction="vertical">
                <span>{record.lastWaba}</span>
                <span>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</span>
              </Space>
            </>
          ) : null;
        },
      },
      {
        title: t('操作'),
        width: 400,
        render(_: string, record: IPhoneListRsp['data'][0]) {
          return (
            <>
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  setClickRowData(record);
                  setVisModalBuyer(true);
                }}
              >
                {t('WhatsappBuyers.关联平台')}
              </Button>
              <WaConcatBtn phone={record.phone} />
            </>
          );
        },
      },
    ];
  }, [t, getRegionLabel]);

  return (
    <Container>
      <Card
        title={t('WhatsappBuyers.WA买家列表')}
        className={css`
          .ant-card-head-title {
            padding: 12px 0;
          }
          .ant-card-extra {
            padding: 12px 0;
          }
        `}
        extra={
          <Button type="primary" onClick={() => setVisModalUpload(true)}>
            {t('WhatsappBuyers.批量添加WA买家')}
          </Button>
        }
      >
        <Spin spinning={spinning}>
          <Row justify="end" style={{ marginBottom: '16px', marginTop: '-4px' }}>
            <Col>
              <Space>
                <Select
                  value={platformFilter}
                  onChange={(value) => {
                    if (value === undefined) {
                      setRegionFilter(value);
                    }
                    setPlatformFilter(value);
                  }}
                  style={{
                    width: '180px',
                  }}
                  placeholder={t('WhatsappBuyers.选择平台')}
                  allowClear
                  options={[{ value: 'SHOPEE' }, { value: 'LAZADA' }]}
                />
                <Select
                  disabled={!platformFilter}
                  value={regionFilter}
                  onChange={setRegionFilter}
                  style={{
                    width: '180px',
                  }}
                  placeholder={t('WhatsappBuyers.选择站点')}
                  allowClear
                  options={
                    platformFilter
                      ? regionsMap[toLowerCase(platformFilter)].map((v) => ({
                          value: v,
                          label: getRegionLabel(v),
                        }))
                      : []
                  }
                />
                <Input.Search
                  style={{
                    width: '360px',
                  }}
                  onSearch={setKeywordFilter}
                  prefix={<SearchOutlined />}
                  placeholder={t('WhatsappBuyers.搜索提示')}
                  enterButton={t('WhatsApp.搜索')}
                  allowClear
                />
              </Space>
            </Col>
          </Row>

          <Table
            columns={columns}
            {...tableProps}
            pagination={{
              ...tableProps.pagination,
              showTotal: (x) => t('WhatsApp.共x项', { x }),
              showQuickJumper: true,
            }}
            rowClassName={(_, index) => {
              if (index === clickRowIndx) {
                return `${RowStyle} ant-table-row-selected`;
              }
              return RowStyle;
            }}
            onRow={(record, index) => {
              return {
                onClick: () => {
                  if (editing) {
                    return;
                  }
                  if (index !== undefined) {
                    setClickRowIndx(index);
                  }
                  setClickRowData(record);
                  setVisDrawer(true);
                },
              };
            }}
            summary={() =>
              tableProps.pagination.total >= 1000 ? (
                <tr>
                  <td colSpan={columns.length} style={{ borderBottom: 'none' }}>
                    <MaxBuyersTip>
                      {t('WhatsappBuyers.仅支持查看x条买家数据', { num: 1000 })}
                    </MaxBuyersTip>
                  </td>
                </tr>
              ) : null
            }
          />
          {/* pagination */}
          <DetailDrawer
            detail={clickRowData!}
            setVisModalBuyer={setVisModalBuyer}
            visDrawer={visDrawer}
            setVisDrawer={setVisDrawer}
            setRefreshAgain={setRefreshAgain}
          />

          <ModalBind
            phone={clickRowData?.phone}
            visModalBuyer={visModalBuyer}
            setVisModalBuyer={setVisModalBuyer}
            setRefreshAgain={setRefreshAgain}
            setVisDrawer={setVisDrawer}
          />

          <ModalUpload
            visModalUpload={visModalUpload}
            setVisModalUpload={setVisModalUpload}
            setRefreshAgain={setRefreshAgain}
          />
        </Spin>
      </Card>
    </Container>
  );
};

export default WhatsappBuyers;
